import React, { useState, useRef, useEffect } from "react";
import { Button, Form, FormControl, Image } from "react-bootstrap";
import { BsUpload } from "react-icons/bs";
import { debounce } from "./utils";
import { uploadNewImage, resetOverlayImageCache } from "./ApiCaller";
import LogoSlider from './LogoSlider';
import formConfig from './formConfig.json';

const VALID_EXTENSIONS = ['jpg', 'jpeg', 'png', 'psd', 'ai', 'pdf'];

function ImageUploader({ setUploadedImageUrl, setUploadedImageFile, showAlert, placementImage = "", onImageUploadComplete, className, formConfig,clientId, generateAllMockups }) {
  const fileInputRef = useRef();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrlLocal] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [isImageUploaded, setImageUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [showUploadBox, setShowUploadBox] = useState(true);

  useEffect(() => {
    if (placementImage && (placementImage.endsWith('.jpg') || placementImage.endsWith('.png'))) {
      setUploadedImage(placementImage);
      setUploadedImageUrlLocal(placementImage);
      setImageUploaded(true);
      callNewApiWithImage(placementImage).then(() => {
        setImageUploaded(true);
      });
    }
  }, [placementImage]);

  const onFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (!isValidFileType(file.name)) {
        handleUploadFailure("Invalid file type. Please upload JPG, PNG, PSD, AI, or PDF files.");
        return;
      }

      setErrorMessage(null);
      setIsLoading(true);
      try {
        resetOverlayImageCache();
        const response = await uploadNewImage(file);

        if (response && response.id && response.uri) {
          console.log('ImageUploader: Setting uploaded image URL:', response.uri);
          handleUploadSuccess(response.id, response.uri, file);
        } else {
          throw new Error('Invalid response from overlay-images API');
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        handleUploadFailure("Failed to upload image.");
      } finally {
        setIsLoading(false);
      }
    } else {
      handleUploadFailure("No file selected.");
    }
  };
 
  const handleUploadSuccess = (id, uri, file) => {
    setUploadedImageUrl(uri);
    setUploadedImageId(id);
    setUploadedImage(uri);
    setUploadedImageFile(file);
    setImageUploaded(true);
    setShowUploadBox(false);
    onImageUploadComplete(id, uri);  // Make sure this is called last
  };

  const handleUploadFailure = (errorMessage) => {
    setErrorMessage(errorMessage);
    showAlert(errorMessage);
    setImageUploaded(false);
    setUploadedImage(null);
    setUploadedImageUrl(null);
    setUploadedImageFile(null);
    setUploadedImageId(null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  
  const onDragLeave = () => {
    setDragging(false);
  };
  
  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer.files;
    
    if (files && files[0]) {
      const file = files[0];
      
      if (!isValidFileType(file.name)) {
        handleUploadFailure("Invalid file type. Please upload JPG, PNG, PSD, AI, or PDF files.");
        return;
      }

      setErrorMessage(null);
      setIsLoading(true);
      
      try {
        resetOverlayImageCache();
        const response = await uploadNewImage(file);

        if (response && response.id && response.uri) {
          handleUploadSuccess(response.id, response.uri, file);
        } else {
          throw new Error('Invalid response from overlay-images API');
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        handleUploadFailure("Failed to upload image.");
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const callNewApiWithImage = async (imageUrl) => {
    setIsLoading(true);
    try {
      resetOverlayImageCache();
      const response = await uploadNewImage(imageUrl);
      if (response && response.id && response.uri) {
        handleUploadSuccess(response.id, response.uri, null);
      } else {
        throw new Error('Invalid response from overlay-images API');
      }
    } catch (error) {
      console.error("Error uploading image to new API:", error);
      handleUploadFailure("Failed to upload image, please save to computer and upload again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const onPaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        const reader = new FileReader();
        reader.onload = (event) => {
          setUploadedImageUrl(event.target.result);
          setUploadedImage(event.target.result);
        };
        reader.readAsDataURL(blob);
        setUploadedImageFile(blob);
        setImageUploaded(true);
        break;
      }
    }
  };
  
  const handleUploadIconClick = () => {
    fileInputRef.current.click();
  };

  const [inputValue, setInputValue] = useState("");

  const isValidFileType = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    return VALID_EXTENSIONS.includes(extension);
  };

  const debouncedOnInputChange = debounce(async (value) => {
    setUploadedImageUrlLocal(value);
    setUploadedImageFile(null);
    setImageUploaded(true);
    if (isValidFileType(value)) {
      await callNewApiWithImage(value);
    } else {
      console.error('Invalid file URL. URL must end with .jpg, .jpeg, .png, .psd, .ai, or .pdf');
      showAlert("Invalid file URL. Please use JPG, PNG, PSD, AI, or PDF files.");
    }
  }, 1000);

  const onInputChange = (e) => {
    const inputValue = e.target.value;
    setInputValue(inputValue);
    debouncedOnInputChange(inputValue);
  };

  useEffect(() => {
    setUploadedImageUrl(uploadedImageUrl);
  }, [uploadedImageUrl, setUploadedImageUrl]);

  useEffect(() => {
    if (uploadedImageUrl) {
      const img = new window.Image();
      img.src = uploadedImageUrl;
      img.onload = () => setUploadedImage(uploadedImageUrl);
      
      img.onerror = () => console.log("Invalid image URL");
    } else {
      setUploadedImage(null);
    }
  }, [uploadedImageUrl]);

  const handleLogoSelect = (logo) => {
    setUploadedImage(logo.url);
    setUploadedImageUrlLocal(logo.url);
    setImageUploaded(true);
    setUploadedImageId(logo.id);
    setShowUploadBox(false);
    onImageUploadComplete(logo.id, logo.url);  // Keep this for normal logo selection handling

    // Check if we should auto-generate mockups
    if (formConfig?.autoGenerateMockups) {
      generateAllMockups(logo.id, logo.url);
    }
  };

  const handleChangeLogoClick = () => {
    setShowUploadBox(true);
  };

  return (
    <div className={`container mx-auto py-2 px-0 sm:p-4 ${className}`}>
      {isImageUploaded && !showUploadBox ? (
        <div className="flex flex-col items-center">
          <img src={uploadedImage} alt="Selected logo" className="w-12 h-12 sm:w-16 sm:h-16 object-contain mb-1 sm:mb-2" />
          <div className="w-full flex justify-center">
            <button 
              onClick={handleChangeLogoClick}
              className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors underline"
            >
              Replace image
            </button>
          </div>
        </div>
      ) : (
        <>
          {formConfig?.showUploadBox && (
            <div className="w-full">
              <label
                htmlFor="dropzone-file"
                className={`flex flex-col items-center justify-center w-full sm:h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 ${
                  dragging ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-100'
                }`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-24 w-24 sm:h-32 sm:w-32 border-t-2 border-b-2 border-gray-900"></div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center pt-3 pb-4 sm:pt-5 sm:pb-6">
                    <BsUpload className="w-8 h-8 sm:w-10 sm:h-10 mb-2 sm:mb-3 text-gray-400" />
                    <p className="mb-1 sm:mb-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG, PSD, AI, or PDF
                    </p>
                    {formConfig?.hideUploadBoxInputUrl !== true && (
                      <div className="flex justify-center w-4/5 mx-auto mt-2 sm:mt-4">
                        <FormControl
                          type="text"
                          value={uploadedImageUrl || ''}
                          placeholder={dragging ? "Drop the image here" : "Enter image URL"}
                          onChange={onInputChange}
                          onPaste={onPaste}
                          autoComplete="on"
                          className="w-full text-sm"
                        />
                      </div>
                    )}
                    {errorMessage && (
                      <p className="text-red-500 text-xs sm:text-sm mt-2">{errorMessage}</p>
                    )}
                  </div>
                )}
                <input
                  id="dropzone-file"
                  type="file"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={onFileChange}
                />
              </label>
            </div>
          )}
          
          {formConfig?.showUploadBox && formConfig?.showLogoPicker && (
            <div className="text-center my-1">
              <span className="px-2 bg-white text-sm text-gray-500">Or</span>
            </div>
          )}
          
          {formConfig?.showLogoPicker && (
            <LogoSlider 
              onLogoSelect={handleLogoSelect}
              clientId={clientId} 
            />
          )}

          {formConfig?.CustomBanner && (
            <div className="w-full mt-4">
              <img 
                src={formConfig.CustomBanner} 
                alt="Custom Banner"
                className="w-full h-auto object-contain"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ImageUploader;
